import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import { Flex } from '@grubhubprod/gh-cookbook-react';
import { InvoiceRecipientForm } from 'src/components/enterprise/EnterpriseInvoiceRecipients/EnterpriseInvoiceRecipientForm';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import {
  DEBOUNCE_SEARCH_TIMING,
  getItem,
  InvoiceRecipientAutocompleteProps,
  useGetRecipients,
} from 'src/components/enterprise/EnterpriseInvoiceRecipients/InvoiceRecipientAutocomplete.utils';
import {
  StyledDropdownButton,
  StyledDropdownSquare,
} from 'src/utilities/sharedStyledComponents';

export function InvoiceRecipientAutocomplete({
  name,
  corpId,
  onSelect,
  children,
}: InvoiceRecipientAutocompleteProps): JSX.Element {
  const {
    watch,
    formState: { dirtyFields },
  } = useFormContext<InvoiceRecipientForm>();

  const dropdownRef = useRef(null);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const debouncedSearchTerm = useDebouncedValue(
    watch(name, '') as string,
    DEBOUNCE_SEARCH_TIMING
  );

  const { recipients } = useGetRecipients(
    corpId,
    debouncedSearchTerm,
    dirtyFields[name]
  );

  useEffect(() => {
    if (showMenu && recipients.length === 0) {
      dropdownRef.current.close();
    }
  }, [showMenu, recipients.length]);

  useEffect(() => {
    if (recipients.length > 0) {
      dropdownRef.current.open();
    }
  }, [recipients.length]);

  return (
    <Dropdown
      ref={dropdownRef}
      menuWidth="480px"
      toggle={() => <>{children}</>}
      onToggle={(toggle) => setShowMenu(toggle)}
      menu={({ show }) =>
        show && (
          <Flex
            flexDirection="column"
            bg="globals.white"
            zIndex={10}
            position="relative"
            width="100%"
          >
            {recipients.map((recipient) => (
              <StyledDropdownSquare
                key={recipient.invoice_contact_id}
                inset="x-sm"
                borderBottom="1px solid"
                borderColor="lightLine"
              >
                <StyledDropdownButton
                  block
                  variant="tertiary"
                  type="button"
                  onClick={() => {
                    onSelect(recipient);
                  }}
                >
                  {getItem(name, recipient)}
                </StyledDropdownButton>
              </StyledDropdownSquare>
            ))}
          </Flex>
        )
      }
    />
  );
}
